import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { createUrl, queryToParams } from './api.util';
import {
  IDepartment,
  IEmployee,
  ITeam,
  ITeamPayload,
  ITeamType,
} from '../model/employee.model';
import { INotification } from '../model/notification.model';
import { QueryResult, IQueryFilter } from '../model/query.filter.class';
import { LoadingService } from './loading.service';
import { ICustomCharge } from '../model/machine.model';

export interface IFileUploadResponse {
  success: boolean;
  data?: { url?: string } | string;
  url?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private path = 'admin';

  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
  ) { }

  getEmployees(query: IQueryFilter): Observable<{
    success: boolean;
    data: QueryResult<IEmployee>;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: QueryResult<IEmployee>;
        message: string;
      }>(createUrl(this.path, 'get-employees'), {
        params: queryToParams(query),
      }),
    );
  }

  uploadFile(formData: FormData, folder?: string): Observable<IFileUploadResponse> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<IFileUploadResponse>(
        createUrl(this.path, 'upload'),
        formData,
        folder ? { headers: new HttpHeaders({ folder }) } : {},
      ),
    );
  }

  getAllEmployees(query?: {
    filters?: any;
    status?: string;
    search?: string;
    sort?: string;
    order?: string;
    [key: string]: any;
  }): Observable<{
    success: boolean;
    data: IEmployee[] | QueryResult<IEmployee>;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: IEmployee[] | QueryResult<IEmployee>;
        message: string;
      }>(createUrl(this.path, 'get-all-employees'), {
        params: queryToParams(query || {}),
      }),
    );
  }

  getEmployee(
    id: string,
  ): Observable<{ success: boolean; data: IEmployee; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: IEmployee;
        message: string;
      }>(createUrl(this.path, 'get-employee', id)),
    );
  }

  addEmployee(
    formData: FormData,
  ): Observable<{ success: boolean; data: IEmployee; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        data: IEmployee;
        message: string;
      }>(createUrl(this.path, 'add-employee'), formData),
    );
  }

  updateEmployee(
    id: string,
    formData: FormData,
  ): Observable<{ success: boolean; data: IEmployee; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.put<{
        success: boolean;
        data: IEmployee;
        message: string;
      }>(createUrl(this.path, 'update-employee', id), formData),
    );
  }

  deleteEmployee(
    id: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.delete<{ success: boolean; message: string }>(
        createUrl(this.path, 'delete-employee', id),
      ),
    );
  }

  switchToEmployee(employeeId: string): Observable<{
    success: boolean;
    data: IEmployee & {
      accessToken: string;
      refreshToken?: string;
      tokenType?: string;
      isImpersonating: boolean;
    };
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        data: IEmployee & {
          accessToken: string;
          refreshToken?: string;
          tokenType?: string;
          isImpersonating: boolean;
        };
        message: string;
      }>(createUrl(this.path, 'switch-to-employee'), { employeeId }),
    );
  }

  switchBackToAdmin(): Observable<{
    success: boolean;
    data: IEmployee & {
      accessToken: string;
      refreshToken?: string;
      tokenType?: string;
      isImpersonating: boolean;
    };
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        data: IEmployee & {
          accessToken: string;
          refreshToken?: string;
          tokenType?: string;
          isImpersonating: boolean;
        };
        message: string;
      }>(createUrl(this.path, 'switch-back-to-admin'), {}),
    );
  }

  getTeamTypes(): Observable<{
    success: boolean;
    data: ITeamType[];
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: ITeamType[];
        message: string;
      }>(createUrl(this.path, 'get-team-types')),
    );
  }

  addTeamType(data: {
    name: string;
  }): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        message: string;
      }>(createUrl(this.path, 'add-team-type'), data),
    );
  }

  deleteTeamType(
    id: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.delete<{ success: boolean; message: string }>(
        createUrl(this.path, 'delete-team-type', id),
      ),
    );
  }

  getDepartment(type?: string): Observable<{
    success: boolean;
    data: QueryResult<IDepartment>;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: QueryResult<IDepartment>;
        message: string;
      }>(createUrl(this.path, 'get-all-department'), {
        params: queryToParams({ type }),
      }),
    );
  }

  addDepartment(data: {
    name: string;
  }): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        message: string;
      }>(createUrl(this.path, 'add-department'), data),
    );
  }

  deleteDepartment(
    id: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.delete<{ success: boolean; message: string }>(
        createUrl(this.path, 'delete-department', id),
      ),
    );
  }

  getTeams(query: IQueryFilter): Observable<{
    success: boolean;
    data: QueryResult<ITeam>;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: QueryResult<ITeam>;
        message: string;
      }>(createUrl(this.path, 'get-teams'), {
        params: queryToParams(query),
      }),
    );
  }

  getTeam(id: string): Observable<{
    success: boolean;
    data: ITeam;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: ITeam;
        message: string;
      }>(createUrl(this.path, 'get-team', id)),
    );
  }

  addTeam(data: ITeamPayload): Observable<{
    success: boolean;
    data: ITeam;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        data: ITeam;
        message: string;
      }>(createUrl(this.path, 'create-team'), data),
    );
  }

  updateTeam(
    id: string,
    data: ITeamPayload,
  ): Observable<{
    success: boolean;
    data: ITeam;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.put<{
        success: boolean;
        data: ITeam;
        message: string;
      }>(createUrl(this.path, 'update-team', id), data),
    );
  }

  deleteTeam(id: string): Observable<{
    success: boolean;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.delete<{
        success: boolean;
        message: string;
      }>(createUrl(this.path, 'delete-team', id)),
    );
  }

  getTechnicalSpecificationRows(
    type: string,
    machineId?: string,
    productionOrderId?: string,
  ): Observable<{
    success: boolean;
    data: any;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: any;
        message: string;
      }>(createUrl(this.path, 'get-technical-specification-rows', type), {
        params: queryToParams({ machineId, productionOrderId }),
      }),
    );
  }

  getCustomCharges(query: IQueryFilter): Observable<{
    success: boolean;
    data: QueryResult<ICustomCharge>;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: QueryResult<ICustomCharge>;
        message: string;
      }>(createUrl(this.path, 'get-custom-charges'), {
        params: queryToParams(query),
      }),
    );
  }

  getNotifications(query: any): Observable<{
    success: boolean;
    data: QueryResult<INotification>;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.get<{
        success: boolean;
        data: QueryResult<INotification>;
        message: string;
      }>(createUrl(this.path, 'get-notifications'), {
        params: queryToParams(query),
      }),
    );
  }

  addCustomCharge(data: Omit<ICustomCharge, 'id'>): Observable<{
    success: boolean;
    data: ICustomCharge;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{
        success: boolean;
        data: ICustomCharge;
        message: string;
      }>(createUrl(this.path, 'add-custom-charges'), data),
    );
  }

  updateCustomCharge(
    id: string,
    data: Partial<ICustomCharge>,
  ): Observable<{
    success: boolean;
    data: ICustomCharge;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.put<{
        success: boolean;
        data: ICustomCharge;
        message: string;
      }>(createUrl(this.path, 'update-custom-charges', id), data),
    );
  }

  deleteCustomCharge(id: string): Observable<{
    success: boolean;
    message: string;
  }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.delete<{
        success: boolean;
        message: string;
      }>(createUrl(this.path, 'delete-custom-charges', id)),
    );
  }

  markNotificationsAsRead(data: {
    notificationIds: string[];
  }): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.put<{ success: boolean; message: string }>(
        createUrl(this.path, 'mark-notifications-as-read'),
        data,
      ),
    );
  }
}
